import React, { useState, useEffect, useRef } from "react";
import ReCaptcha from "../components/ReCaptcha";
import { sendReservationEmail } from "../services/emailService";
import SEO from "../components/SEO";

/* ─── Opening Hours Data ──────────────────────────────────────── */
const OPENING_HOURS = [
  { day: "Monday – Thursday", time: "11:00 AM – 10:00 PM", peak: false },
  { day: "Friday – Saturday", time: "11:00 AM – 11:00 PM", peak: true },
  { day: "Sunday", time: "11:00 AM – 09:00 PM", peak: false },
];

const GUEST_OPTIONS = [
  "1 Guest",
  "2 Guests",
  "3 Guests",
  "4 Guests",
  "5 Guests",
  "6 Guests",
  "7–10 Guests",
  "10+ Guests",
];

const DINING_PREFERENCES = [
  { id: "indoor", label: "Indoor", icon: "🏛️" },
  { id: "outdoor", label: "Outdoor", icon: "🌿" },
  { id: "private", label: "Private Room", icon: "🔒" },
  { id: "bar", label: "Bar Side", icon: "🥂" },
];

const TIME_SLOTS = [
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "01:00 PM",
  "01:30 PM",
  "02:00 PM",
  "02:30 PM",
  "06:00 PM",
  "06:30 PM",
  "07:00 PM",
  "07:30 PM",
  "08:00 PM",
  "08:30 PM",
  "09:00 PM",
  "09:30 PM",
  "10:00 PM",
];

const todayStr = () => new Date().toISOString().split("T")[0];

/* ─── Info Card ───────────────────────────────────────────────── */
const InfoCard = ({ icon, title, children }) => (
  <div className="flex gap-4 items-start group">
    <div className="w-10 h-10 rounded-lg bg-[#9b1c2e]/20 border border-[#9b1c2e]/30 flex items-center justify-center flex-shrink-0 group-hover:bg-[#9b1c2e]/30 transition-colors duration-300">
      {icon}
    </div>
    <div>
      <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#c0424e] mb-1">
        {title}
      </p>
      <div className="text-[13px] text-gray-300 leading-relaxed font-sans">
        {children}
      </div>
    </div>
  </div>
);

/* ─── Step Dot ────────────────────────────────────────────────── */
const StepDot = ({ step, current, label }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold border-2 transition-all duration-300 ${
        current > step
          ? "bg-[#9b1c2e] border-[#9b1c2e] text-white"
          : current === step
            ? "bg-white border-[#9b1c2e] text-[#9b1c2e]"
            : "bg-transparent border-white/20 text-gray-500"
      }`}
    >
      {current > step ? (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      ) : (
        step
      )}
    </div>
    <span
      className={`text-[10px] uppercase tracking-wider font-semibold ${current === step ? "text-white" : "text-gray-500"}`}
    >
      {label}
    </span>
  </div>
);

/* ─── Success Screen ──────────────────────────────────────────── */
const SuccessScreen = ({ form, onReset }) => (
  <div className="flex flex-col items-center justify-center py-12 px-6 text-center animate-fade-in-up">
    <div className="relative mb-8">
      <div className="w-24 h-24 rounded-full bg-[#9b1c2e]/15 border-2 border-[#9b1c2e]/40 flex items-center justify-center mx-auto">
        <div className="w-16 h-16 rounded-full bg-[#9b1c2e]/25 border-2 border-[#9b1c2e]/60 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-[#9b1c2e]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      </div>
    </div>
    <p className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#c0424e] mb-3">
      Reservation Confirmed
    </p>
    <h2 className="text-[28px] sm:text-[34px] font-bold text-white uppercase tracking-wide leading-tight mb-4">
      See You Soon,
      <br />
      <span className="text-[#9b1c2e]">{form.name.split(" ")[0]}!</span>
    </h2>
    <p className="font-sans text-gray-400 text-sm leading-relaxed max-w-sm mb-8">
      Your table has been reserved. A confirmation will be sent to{" "}
      <span className="text-white font-medium">{form.email}</span>. We look
      forward to serving you.
    </p>
    <div className="bg-white/5 border border-white/10 rounded-xl px-6 py-4 mb-8 text-left w-full max-w-sm space-y-2">
      {[
        ["Date", form.date],
        ["Time", form.time],
        ["Guests", form.guests],
        ["Seating", form.preference || "Any"],
      ].map(([k, v]) => (
        <div key={k} className="flex justify-between text-sm">
          <span className="text-gray-500 font-sans">{k}</span>
          <span className="text-white font-medium font-sans capitalize">
            {v}
          </span>
        </div>
      ))}
    </div>
    <button
      onClick={onReset}
      className="font-sans inline-flex items-center gap-2 px-8 py-3 border border-[#9b1c2e] text-[#c0424e] text-[12px] font-bold tracking-[0.2em] uppercase hover:bg-[#9b1c2e] hover:text-white transition-all duration-300 rounded"
    >
      Make Another Reservation
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M17 8l4 4m0 0l-4 4m4-4H3"
        />
      </svg>
    </button>
  </div>
);

/* ─── Main Page ───────────────────────────────────────────────── */
const ReservationPage = () => {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    guests: "",
    preference: "",
    occasion: "",
    specialRequest: "",
  });
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const captchaRef = useRef(null);

  const update = (field, val) => {
    setForm((p) => ({ ...p, [field]: val }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  useEffect(() => {
    if (formRef.current)
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      if (!form.name.trim()) errs.name = "Full name is required.";
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        errs.email = "Valid email is required.";
      if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10)
        errs.phone = "Valid phone number is required.";
    }
    if (s === 2) {
      if (!form.date) errs.date = "Please choose a date.";
      if (!form.time) errs.time = "Please choose a time slot.";
      if (!form.guests) errs.guests = "Please select number of guests.";
    }
    if (s === 3) {
      if (!captchaToken) {
        setCaptchaError("Please complete the reCAPTCHA verification to confirm your reservation.");
        errs.captcha = "reCAPTCHA required";
      }
    }
    return errs;
  };

  const nextStep = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setStep((s) => s + 1);
  };

  const prevStep = () => setStep((s) => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateStep(step);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      await sendReservationEmail(form, captchaToken);
      setSubmitted(true);
    } catch (err) {
      console.error("Failed to send reservation email:", err);
      setCaptchaError("Failed to submit reservation. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      date: "",
      time: "",
      guests: "",
      preference: "",
      occasion: "",
      specialRequest: "",
    });
    setCaptchaToken("");
    setCaptchaError("");
    captchaRef.current?.reset();
    setErrors({});
    setStep(1);
    setSubmitted(false);
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-white/5 border rounded-lg text-white text-sm placeholder-gray-500 font-sans focus:outline-none transition-all duration-200 ${
      errors[field]
        ? "border-red-500/70 focus:border-red-400"
        : "border-white/10 focus:border-[#9b1c2e] hover:border-white/25"
    }`;

  return (
    <div className="min-h-screen bg-[#0d0005]">
      <SEO
        title="Book a Table & Table Reservation | Barbequeen Restaurant"
        metaTitle="Book a Table & Table Reservation | Barbequeen Restaurant"
        description="Book your table online at Barbequeen Restaurant. Reserve seats for family dining, parties, and special occasions across our outlets in Tamil Nadu."
        keywords="Barbequeen reservation, book a table Salem, restaurant table booking, family dining reservation"
        robots="index, follow"
        canonical="https://barbequeen.com/reservation"
        og={{
          type: "website",
          url: "https://barbequeen.com/reservation",
          title: "Book a Table | Barbequeen Restaurant",
          description: "Reserve your seat at Barbequeen and enjoy an authentic Indian BBQ & biryani feast.",
          image: "https://barbequeen.com/images/home-banner.jpg",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
      />
      {/* ── Hero ── */}
      <section className="relative h-[340px] sm:h-[420px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=1800&auto=format&fit=crop"
          alt="Barbequeen fine dining atmosphere"
          className="absolute inset-0 w-full h-full object-cover object-center animate-subtle-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[#0d0005]" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <p className="text-[11px] tracking-[0.35em] uppercase font-bold text-[#c0424e] mb-4 animate-fade-in-up">
            Reserve Your Experience
          </p>
          <h1
            className="text-[40px] sm:text-[58px] lg:text-[68px] font-bold text-white uppercase leading-none tracking-wide animate-fade-in-up"
            style={{ animationDelay: "0.1s" }}
          >
            Book a Table
          </h1>
          <div
            className="mt-5 w-12 h-0.5 bg-[#9b1c2e] animate-fade-in-up"
            style={{ animationDelay: "0.2s" }}
          />
          <p
            className="font-sans mt-5 text-sm text-gray-300 max-w-md leading-relaxed animate-fade-in-up"
            style={{ animationDelay: "0.25s" }}
          >
            Reserve your seat at Barbequeen and enjoy an authentic Indian BBQ
            experience crafted with passion.
          </p>
        </div>
      </section>

      {/* ── Main Content ── */}
      <section className="relative py-16 lg:py-24">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* ── Left Info Panel ── */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-8">
                {/* Opening Hours */}
                <div className="bg-[#1a0008] border border-white/8 rounded-2xl p-7 relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-36 h-36 bg-[#9b1c2e]/10 rounded-full blur-2xl pointer-events-none" />
                  <p className="text-[11px] font-bold tracking-[0.25em] text-[#c0424e] uppercase mb-2">
                    Schedule
                  </p>
                  <h2 className="text-[22px] font-bold text-white uppercase tracking-wide mb-6">
                    Opening Hours
                  </h2>
                  <div className="space-y-3">
                    {OPENING_HOURS.map((row, i) => (
                      <div
                        key={i}
                        className={`flex justify-between items-center py-3 px-4 rounded-lg ${
                          row.peak
                            ? "bg-[#9b1c2e]/15 border border-[#9b1c2e]/30"
                            : "bg-white/4 border border-white/6"
                        }`}
                      >
                        <div>
                          <p className="text-[12px] font-semibold text-gray-200">
                            {row.day}
                          </p>
                          {row.peak && (
                            <span className="text-[10px] font-bold tracking-wider text-[#c0424e] uppercase">
                              Peak Hours
                            </span>
                          )}
                        </div>
                        <p className="font-sans text-[12px] text-gray-300 font-medium">
                          {row.time}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact */}
                <div className="bg-[#1a0008] border border-white/8 rounded-2xl p-7 space-y-6 relative overflow-hidden">
                  <div className="absolute -bottom-10 -left-10 w-36 h-36 bg-[#9b1c2e]/8 rounded-full blur-2xl pointer-events-none" />
                  <p className="text-[11px] font-bold tracking-[0.25em] text-[#c0424e] uppercase mb-2">
                    Get in Touch
                  </p>
                  <InfoCard
                    icon={
                      <svg
                        className="w-5 h-5 text-[#c0424e]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    }
                    title="Phone"
                  >
                    <a
                      href="tel:+919597228788"
                      className="hover:text-[#c0424e] transition-colors"
                    >
                      +91-95972-28788
                    </a>
                  </InfoCard>
                  <InfoCard
                    icon={
                      <svg
                        className="w-5 h-5 text-[#c0424e]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    }
                    title="Email"
                  >
                    <a
                      href="mailto:feedback@barbequeen.com"
                      className="hover:text-[#c0424e] transition-colors"
                    >
                      feedback@barbequeen.com
                    </a>
                  </InfoCard>
                  <InfoCard
                    icon={
                      <svg
                        className="w-5 h-5 text-[#c0424e]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    }
                    title="Location"
                  >
                    32, Valmiki Street, Subramaniya Nagar, Salem – 636 005.
                  </InfoCard>
                </div>

                {/* Walk-in note */}
                <div className="bg-[#9b1c2e]/10 border border-[#9b1c2e]/25 rounded-xl px-5 py-4 flex gap-3 items-start">
                  <svg
                    className="w-4 h-4 text-[#c0424e] flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="font-sans text-[12px] text-gray-400 leading-relaxed">
                    Walk-ins are welcome, but reservations are{" "}
                    <span className="text-white font-medium">
                      strongly recommended
                    </span>{" "}
                    on weekends to guarantee your preferred table and full menu
                    availability.
                  </p>
                </div>
              </div>
            </aside>

            {/* ── Right Form Panel ── */}
            <div className="lg:col-span-8" ref={formRef}>
              <div className="bg-[#1a0008] border border-white/8 rounded-2xl overflow-hidden relative">
                {/* Top accent bar */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#9b1c2e] to-transparent" />

                {/* Header */}
                <div className="px-8 pt-10 pb-6 border-b border-white/6">
                  <p className="text-[11px] font-bold tracking-[0.25em] text-[#c0424e] uppercase mb-2">
                    Online Reservation
                  </p>
                  <h2 className="text-[26px] sm:text-[32px] font-bold text-white uppercase tracking-wide">
                    Reserve Your Table{" "}
                    <span className="text-[#9b1c2e]">with Ease</span>
                  </h2>
                </div>

                {/* Step Indicator */}
                {!submitted && (
                  <div className="px-8 py-5 flex items-center border-b border-white/6">
                    <StepDot step={1} current={step} label="Details" />
                    <div
                      className={`flex-1 h-0.5 mx-2 transition-colors duration-500 ${step > 1 ? "bg-[#9b1c2e]" : "bg-white/10"}`}
                    />
                    <StepDot step={2} current={step} label="Date & Time" />
                    <div
                      className={`flex-1 h-0.5 mx-2 transition-colors duration-500 ${step > 2 ? "bg-[#9b1c2e]" : "bg-white/10"}`}
                    />
                    <StepDot step={3} current={step} label="Preferences" />
                  </div>
                )}

                {/* Form Body */}
                <div className="px-8 py-8">
                  {submitted ? (
                    <SuccessScreen form={form} onReset={resetForm} />
                  ) : (
                    <form onSubmit={handleSubmit} noValidate>
                      {/* Step 1 */}
                      {step === 1 && (
                        <div className="space-y-5 animate-fade-in-up">
                          <p className="font-sans text-[13px] text-gray-400 mb-2">
                            Enter your personal contact details to continue.
                          </p>

                          <div>
                            <label
                              className="block text-[11px] font-bold tracking-[0.15em] text-gray-300 uppercase mb-1.5"
                              htmlFor="res-name"
                            >
                              Full Name{" "}
                              <span className="text-[#9b1c2e]">*</span>
                            </label>
                            <input
                              id="res-name"
                              type="text"
                              className={inputClass("name")}
                              placeholder="Your full name"
                              value={form.name}
                              onChange={(e) => update("name", e.target.value)}
                              autoComplete="name"
                            />
                            {errors.name && (
                              <p className="font-sans text-[11px] text-red-400 mt-1">
                                {errors.name}
                              </p>
                            )}
                          </div>

                          <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                              <label
                                className="block text-[11px] font-bold tracking-[0.15em] text-gray-300 uppercase mb-1.5"
                                htmlFor="res-email"
                              >
                                Email <span className="text-[#9b1c2e]">*</span>
                              </label>
                              <input
                                id="res-email"
                                type="email"
                                className={inputClass("email")}
                                placeholder="example@email.com"
                                value={form.email}
                                onChange={(e) =>
                                  update("email", e.target.value)
                                }
                                autoComplete="email"
                              />
                              {errors.email && (
                                <p className="font-sans text-[11px] text-red-400 mt-1">
                                  {errors.email}
                                </p>
                              )}
                            </div>
                            <div>
                              <label
                                className="block text-[11px] font-bold tracking-[0.15em] text-gray-300 uppercase mb-1.5"
                                htmlFor="res-phone"
                              >
                                Phone Number{" "}
                                <span className="text-[#9b1c2e]">*</span>
                              </label>
                              <input
                                id="res-phone"
                                type="tel"
                                className={inputClass("phone")}
                                placeholder="+91 98765 43210"
                                value={form.phone}
                                onChange={(e) =>
                                  update("phone", e.target.value)
                                }
                                autoComplete="tel"
                              />
                              {errors.phone && (
                                <p className="font-sans text-[11px] text-red-400 mt-1">
                                  {errors.phone}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex justify-end pt-4">
                            <button
                              type="button"
                              onClick={nextStep}
                              className="font-sans inline-flex items-center gap-2 px-8 py-3 bg-[#9b1c2e] hover:bg-[#7d1624] text-white text-[12px] font-bold tracking-[0.2em] uppercase transition-all duration-200 rounded hover:gap-3.5"
                            >
                              Next Step
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step 2 */}
                      {step === 2 && (
                        <div className="space-y-6 animate-fade-in-up">
                          <p className="font-sans text-[13px] text-gray-400 mb-2">
                            Choose your preferred date, time, and party size.
                          </p>

                          <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                              <label
                                className="block text-[11px] font-bold tracking-[0.15em] text-gray-300 uppercase mb-1.5"
                                htmlFor="res-date"
                              >
                                Reservation Date{" "}
                                <span className="text-[#9b1c2e]">*</span>
                              </label>
                              <input
                                id="res-date"
                                type="date"
                                min={todayStr()}
                                className={`${inputClass("date")} [color-scheme:dark]`}
                                value={form.date}
                                onChange={(e) => update("date", e.target.value)}
                              />
                              {errors.date && (
                                <p className="font-sans text-[11px] text-red-400 mt-1">
                                  {errors.date}
                                </p>
                              )}
                            </div>
                            <div>
                              <label
                                className="block text-[11px] font-bold tracking-[0.15em] text-gray-300 uppercase mb-1.5"
                                htmlFor="res-guests"
                              >
                                Number of Guests{" "}
                                <span className="text-[#9b1c2e]">*</span>
                              </label>
                              <select
                                id="res-guests"
                                className={`${inputClass("guests")} [color-scheme:dark]`}
                                value={form.guests}
                                onChange={(e) =>
                                  update("guests", e.target.value)
                                }
                              >
                                <option value="" disabled>
                                  Select guests
                                </option>
                                {GUEST_OPTIONS.map((g) => (
                                  <option
                                    key={g}
                                    value={g}
                                    className="bg-[#1a0008]"
                                  >
                                    {g}
                                  </option>
                                ))}
                              </select>
                              {errors.guests && (
                                <p className="font-sans text-[11px] text-red-400 mt-1">
                                  {errors.guests}
                                </p>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="block text-[11px] font-bold tracking-[0.15em] text-gray-300 uppercase mb-3">
                              Preferred Time{" "}
                              <span className="text-[#9b1c2e]">*</span>
                            </label>
                            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                              {TIME_SLOTS.map((slot) => (
                                <button
                                  key={slot}
                                  type="button"
                                  onClick={() => update("time", slot)}
                                  className={`font-sans py-2 px-2 text-[11px] font-semibold rounded-lg border transition-all duration-200 ${
                                    form.time === slot
                                      ? "bg-[#9b1c2e] border-[#9b1c2e] text-white shadow-md shadow-[#9b1c2e]/30"
                                      : "bg-white/4 border-white/10 text-gray-300 hover:border-[#9b1c2e]/50 hover:text-white"
                                  }`}
                                >
                                  {slot}
                                </button>
                              ))}
                            </div>
                            {errors.time && (
                              <p className="font-sans text-[11px] text-red-400 mt-2">
                                {errors.time}
                              </p>
                            )}
                          </div>

                          <div className="flex justify-between pt-4">
                            <button
                              type="button"
                              onClick={prevStep}
                              className="font-sans inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-gray-300 text-[12px] font-bold tracking-[0.15em] uppercase hover:border-white/30 hover:text-white transition-all duration-200 rounded"
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M7 16l-4-4m0 0l4-4m-4 4h18"
                                />
                              </svg>
                              Back
                            </button>
                            <button
                              type="button"
                              onClick={nextStep}
                              className="font-sans inline-flex items-center gap-2 px-8 py-3 bg-[#9b1c2e] hover:bg-[#7d1624] text-white text-[12px] font-bold tracking-[0.2em] uppercase transition-all duration-200 rounded hover:gap-3.5"
                            >
                              Next Step
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step 3 */}
                      {step === 3 && (
                        <div className="space-y-6 animate-fade-in-up">
                          <p className="font-sans text-[13px] text-gray-400 mb-2">
                            Almost there! Tell us your seating preference and
                            any special requests.
                          </p>

                          {/* Seating Preference */}
                          <div>
                            <label className="block text-[11px] font-bold tracking-[0.15em] text-gray-300 uppercase mb-3">
                              Seating Preference{" "}
                              <span className="text-gray-500 text-[10px] normal-case tracking-normal font-sans">
                                (optional)
                              </span>
                            </label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              {DINING_PREFERENCES.map((pref) => (
                                <button
                                  key={pref.id}
                                  type="button"
                                  onClick={() =>
                                    update(
                                      "preference",
                                      form.preference === pref.id
                                        ? ""
                                        : pref.id,
                                    )
                                  }
                                  className={`flex flex-col items-center gap-2 py-4 px-3 rounded-xl border text-center transition-all duration-200 ${
                                    form.preference === pref.id
                                      ? "bg-[#9b1c2e]/20 border-[#9b1c2e] text-white shadow-md shadow-[#9b1c2e]/20"
                                      : "bg-white/3 border-white/8 text-gray-400 hover:border-white/20 hover:text-gray-200"
                                  }`}
                                >
                                  <span className="text-2xl">{pref.icon}</span>
                                  <span className="font-sans text-[11px] font-bold tracking-wide">
                                    {pref.label}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Occasion */}
                          <div>
                            <label
                              className="block text-[11px] font-bold tracking-[0.15em] text-gray-300 uppercase mb-1.5"
                              htmlFor="res-occasion"
                            >
                              Occasion{" "}
                              <span className="text-gray-500 text-[10px] normal-case tracking-normal font-sans">
                                (optional)
                              </span>
                            </label>
                            <select
                              id="res-occasion"
                              className={`${inputClass("occasion")} [color-scheme:dark]`}
                              value={form.occasion}
                              onChange={(e) =>
                                update("occasion", e.target.value)
                              }
                            >
                              <option value="" className="bg-[#1a0008]">
                                Select an occasion
                              </option>
                              <option value="birthday" className="bg-[#1a0008]">
                                🎂 Birthday Celebration
                              </option>
                              <option
                                value="anniversary"
                                className="bg-[#1a0008]"
                              >
                                💑 Anniversary
                              </option>
                              <option value="business" className="bg-[#1a0008]">
                                💼 Business Dinner
                              </option>
                              <option value="date" className="bg-[#1a0008]">
                                🥂 Romantic Date
                              </option>
                              <option value="family" className="bg-[#1a0008]">
                                👨‍👩‍👧‍👦 Family Get-Together
                              </option>
                              <option value="friends" className="bg-[#1a0008]">
                                🎉 Friends Gathering
                              </option>
                              <option value="other" className="bg-[#1a0008]">
                                ✨ Other
                              </option>
                            </select>
                          </div>

                          {/* Special Requests */}
                          <div>
                            <label
                              className="block text-[11px] font-bold tracking-[0.15em] text-gray-300 uppercase mb-1.5"
                              htmlFor="res-special"
                            >
                              Special Requests{" "}
                              <span className="text-gray-500 text-[10px] normal-case tracking-normal font-sans">
                                (optional)
                              </span>
                            </label>
                            <textarea
                              id="res-special"
                              rows={4}
                              className={`${inputClass("specialRequest")} resize-none`}
                              placeholder="Dietary requirements, allergies, high chair needed, décor requests…"
                              value={form.specialRequest}
                              onChange={(e) =>
                                update("specialRequest", e.target.value)
                              }
                            />
                          </div>

                          {/* Summary pill */}
                          <div className="bg-white/4 border border-white/8 rounded-xl px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                            {[
                              { label: "Name", value: form.name || "—" },
                              { label: "Guests", value: form.guests || "—" },
                              { label: "Date", value: form.date || "—" },
                              { label: "Time", value: form.time || "—" },
                            ].map((item) => (
                              <div key={item.label}>
                                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-0.5">
                                  {item.label}
                                </p>
                                <p className="font-sans text-[12px] text-white font-medium truncate">
                                  {item.value}
                                </p>
                              </div>
                            ))}
                          </div>

                          {/* Google reCAPTCHA Verification */}
                          <div className="pt-2">
                            <ReCaptcha
                              ref={captchaRef}
                              theme="dark"
                              onChange={(token) => {
                                setCaptchaToken(token);
                                if (token) {
                                  setCaptchaError("");
                                  setErrors((p) => ({ ...p, captcha: "" }));
                                }
                              }}
                              onExpired={() => setCaptchaToken("")}
                              errorMessage={captchaError || errors.captcha}
                            />
                          </div>

                          <div className="flex justify-between pt-2">
                            <button
                              type="button"
                              onClick={prevStep}
                              className="font-sans inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-gray-300 text-[12px] font-bold tracking-[0.15em] uppercase hover:border-white/30 hover:text-white transition-all duration-200 rounded"
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M7 16l-4-4m0 0l4-4m-4 4h18"
                                />
                              </svg>
                              Back
                            </button>
                            <button
                              type="submit"
                              disabled={submitting}
                              className="font-sans inline-flex items-center gap-2 px-8 py-3 bg-[#9b1c2e] hover:bg-[#7d1624] disabled:opacity-60 disabled:cursor-not-allowed text-white text-[12px] font-bold tracking-[0.2em] uppercase transition-all duration-200 rounded"
                            >
                              {submitting ? (
                                <>
                                  <svg
                                    className="w-4 h-4 animate-spin"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth={4}
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                  </svg>
                                  Confirming…
                                </>
                              ) : (
                                <>
                                  Confirm Reservation
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </form>
                  )}
                </div>

                {/* Footer note */}
                {!submitted && (
                  <div className="px-8 py-4 border-t border-white/6 flex items-center gap-2">
                    <svg
                      className="w-3.5 h-3.5 text-gray-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    <p className="font-sans text-[11px] text-gray-500">
                      Your information is secure and will only be used to manage
                      your reservation.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReservationPage;

